import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaPaperPlane,
  FaRobot,
  FaUser,
  FaArrowRight,
  FaWhatsapp,
} from "react-icons/fa";

import "./ChatBot.css";
import ChatHeader from "./components/ChatHeader";
import {
  QUICK_ACTIONS,
  WELCOME_MESSAGE,
  getBotResponse,
  buildWhatsAppLink,
} from "./BotEngine";

export default function ChatBot({ isOpen, onClose }) {
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const panelRef = useRef(null);
  const bodyRef = useRef(null);
  const lastFocusedRef = useRef(null);

  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);

  const [messages, setMessages] = useState([
    {
      id: crypto.randomUUID(),
      sender: "bot",
      ...WELCOME_MESSAGE,
    },
  ]);

  useEffect(() => {
    if (isOpen) {
      lastFocusedRef.current = document.activeElement;
      inputRef.current?.focus();
      return undefined;
    }

    // Return focus to whatever opened the chat (mascot / launcher).
    if (lastFocusedRef.current instanceof HTMLElement) {
      lastFocusedRef.current.focus();
      lastFocusedRef.current = null;
    }
    return undefined;
  }, [isOpen]);

  // Escape closes the panel, Tab is trapped inside it.
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
        return;
      }

      if (e.key !== "Tab" || !panelRef.current) return;

      const focusables = panelRef.current.querySelectorAll(
        'button:not([disabled]),input:not([disabled]),a[href],[tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  // Reliable scroll-to-bottom: drive the scroll container directly (mobile
  // browsers frequently drop smooth scrollIntoView during layout/keyboard shifts).
  const scrollToBottom = (smooth = true) => {
    const body = bodyRef.current;
    if (!body) return;
    const run = () => {
      body.scrollTo({
        top: body.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
      messagesEndRef.current?.scrollIntoView({
        block: "end",
        behavior: smooth ? "smooth" : "auto",
      });
    };
    requestAnimationFrame(run);
    // Second pass after images/wrapping settle.
    setTimeout(run, 120);
    setTimeout(() => {
      if (body) body.scrollTop = body.scrollHeight;
    }, 320);
  };

  useEffect(() => {
    if (!isOpen) return;
    scrollToBottom(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, typing, isOpen]);

  // Keep the composer above the mobile virtual keyboard and re-anchor the log.
  useEffect(() => {
    if (!isOpen) return undefined;

    const vv = window.visualViewport;
    const panel = panelRef.current;

    const apply = () => {
      if (!panel) return;
      if (vv) {
        const keyboard = Math.max(
          0,
          window.innerHeight - vv.height - vv.offsetTop
        );
        panel.style.setProperty("--chat-visual-height", `${vv.height}px`);
        panel.style.setProperty("--chat-keyboard-inset", `${keyboard}px`);
      } else {
        panel.style.setProperty("--chat-visual-height", `${window.innerHeight}px`);
        panel.style.setProperty("--chat-keyboard-inset", "0px");
      }
      scrollToBottom(false);
    };

    apply();

    vv?.addEventListener("resize", apply);
    vv?.addEventListener("scroll", apply);
    window.addEventListener("orientationchange", apply);

    return () => {
      vv?.removeEventListener("resize", apply);
      vv?.removeEventListener("scroll", apply);
      window.removeEventListener("orientationchange", apply);
      panel?.style.removeProperty("--chat-visual-height");
      panel?.style.removeProperty("--chat-keyboard-inset");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const lastMessage = messages[messages.length - 1];
  const lastBotSuggestions =
    lastMessage?.sender === "bot" ? lastMessage.suggestions || [] : [];


  const sendMessage = (text = input) => {
    if (!text.trim()) return;

    setMessages((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        sender: "user",
        type: "text",
        text: text.trim(),
      },
    ]);

    setInput("");
    setTyping(true);
    scrollToBottom(true);

    setTimeout(() => {
      const response = getBotResponse(text);

      setTyping(false);

      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          sender: "bot",
          ...response,
        },
      ]);

      inputRef.current?.focus();
    }, 600);
  };

  return (
    <div
      className="chatbot"
      id="techizen-chat-panel"
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label="Techizen AI assistant chat"
    >

      <ChatHeader onClose={onClose} />

      <div
        className="chatbot-body"
        ref={bodyRef}
        role="log"
        aria-live="polite"
        aria-relevant="additions text"
        aria-label="Conversation with Techizen AI"
        tabIndex={0}
      >

        <div className="quick-actions" role="group" aria-label="Quick actions">
          {QUICK_ACTIONS.map((action) => (
            <button
              key={action}
              type="button"
              onClick={() => sendMessage(action)}
              aria-label={`Ask: ${action}`}
            >
              {action}
            </button>
          ))}
        </div>

        {messages.map((message) => (
          <div
            key={message.id}
            className={`message ${message.sender}`}
          >
            <div className="avatar" aria-hidden="true">
              {message.sender === "bot" ? <FaRobot /> : <FaUser />}
            </div>

            <span className="chat-sr-only">
              {message.sender === "bot" ? "Techizen AI said:" : "You said:"}
            </span>

            <div className="bubble">
              {(message.text || "")
                .split("\n")
                .map((line, index) => (
                  <div key={index}>{line}</div>
                ))}

              {message.links?.length > 0 && (
                <div className="bubble-links">
                  {message.links.map((link) => (
                    <Link
                      key={`${link.to}-${link.label}`}
                      to={link.to}
                      className="bubble-link"
                      onClick={onClose}
                    >
                      {link.label}
                      <FaArrowRight aria-hidden="true" />
                    </Link>
                  ))}
                </div>
              )}

              {message.sender === "bot" && message.whatsapp !== undefined && (
                <a
                  className="bubble-whatsapp"
                  href={buildWhatsAppLink(message.whatsapp)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Talk to our agents on WhatsApp (opens in a new tab)"
                >
                  <FaWhatsapp aria-hidden="true" />
                  Talk to our agents on WhatsApp
                </a>
              )}
            </div>
          </div>
        ))}

        {typing && (
          <div className="message bot">
            <div className="avatar" aria-hidden="true">
              <FaRobot />
            </div>

            <span className="chat-sr-only" role="status">
              Techizen AI is typing
            </span>

            <div className="bubble typing-bubble" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        {!typing && lastBotSuggestions.length > 0 && (
          <div className="suggestion-chips" role="group" aria-label="Suggested questions">
            {lastBotSuggestions.map((chip) => (
              <button
                key={chip}
                type="button"
                className={`suggestion-chip ${
                  chip.toLowerCase().includes("agent") ? "is-agent" : ""
                }`}
                onClick={() => sendMessage(chip)}
                aria-label={`Ask: ${chip}`}
              >
                {chip.toLowerCase().includes("agent") && <FaWhatsapp aria-hidden="true" />}
                {chip}
              </button>
            ))}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>


      <form
        className="chatbot-footer"
        onSubmit={(event) => {
          event.preventDefault();
          sendMessage();
        }}
      >
        <input
          ref={inputRef}
          type="text"
          placeholder="Ask Techizen AI anything..."
          aria-label="Message Techizen AI"
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />

        <button type="submit" disabled={!input.trim()} aria-label="Send message to Techizen AI">
          <FaPaperPlane aria-hidden="true" />
        </button>
      </form>

    </div>
  );
}
